import {ColumnDef, Row} from '@tanstack/react-table';
import DataTableRowActions from "@/components/data-table/data-table-row-actions.tsx";
import {DataTableColumnHeader} from "@/components/data-table/data-table-column-header.tsx";
import {Mentor} from "@/types/Mentor.ts";
import {Criteria} from "@/types/Criteria.ts";

interface MentorsColumnsProps {
    onEdit: (mentor: Mentor) => void;
}

const filterCriteriaList = (row: Row<Mentor>, id: string, filter: string) => {
    const data = String(row.getValue(id)).split(',').map(value => value.trim());
    const filterValues = filter.split(',').map(value => value.trim());

    return filterValues.some(filterValue => {
        return data.includes(filterValue);
    });
}

const accessorFnForCriteriaList = (criteria: Criteria[]) => {
    if (!criteria || criteria.length === 0) {
        return '';
    }
    return criteria.map(criterion => criterion.name).join(', ');
}

const accessorFnForProfileCompletion = (row: Mentor) => {
    const status: string[] = [];

    if (!(row.title && row.university && row.bio && row.region)) {
        status.push("General Information Incomplete");
    }

    if (!row.languages || row.languages.length === 0) {
        status.push("Mentorship Information Incomplete");
    }

    if (!row.timezone) {
        status.push("Availability Information Incomplete");
    }

    if (status.length === 0) {
        status.push("Profile Completed");
    }

    return status.join(",");
}

export const getMentorsColumns = ({
                                      onEdit,
                                  }: MentorsColumnsProps): ColumnDef<Mentor>[] => [
    {
        id: "profileCompletion",
        accessorFn: row => accessorFnForProfileCompletion(row),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="profileCompletion"/>
        ),
        filterFn: (row, id, value) => filterCriteriaList(row, id, String(value)),
        enableSorting: false,
    },
    {
        id: "name",
        accessorFn: row => `${row.user.firstName} ${row.user.lastName}`,
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="name"/>
        ),
        enableHiding: false,
    },
    {
        id: "email",
        accessorFn: row => row.user.email,
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="email" />
        ),
    },
    {
        id: "careerFocus",
        accessorFn: row => row.careerFocus?.name ?? "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="careerFocus"/>
        ),
        filterFn: (row, id, value) => {
            return value.includes(row.getValue(id));
        },
    },
    {
        id: "experience",
        accessorFn: row => row.experience?.name ?? "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="experience"/>
        ),
        filterFn: (row, id, value) => {
            return value.includes(row.getValue(id));
        },
    },
    {
        id: 'disciplines',
        accessorFn: (row) => accessorFnForCriteriaList(row.disciplines),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="disciplines"/>
        ),
        filterFn: (row, id, filter) => filterCriteriaList(row, id, String(filter)),
    },
    {
        id: 'researchExpertises',
        accessorFn: (row) => accessorFnForCriteriaList(row.researchExpertises),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="researchExpertises"/>
        ),
        filterFn: (row, id, filter) => filterCriteriaList(row, id, String(filter)),
    },
    {
        id: 'languages',
        accessorFn: (row) => accessorFnForCriteriaList(row.languages),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="languages"/>
        ),
        filterFn: (row, id, filter) => filterCriteriaList(row, id, String(filter)),
    },
    {
        id: 'socioeconomicDisadvantages',
        accessorFn: (row) => accessorFnForCriteriaList(row.socioeconomicDisadvantages),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="socioeconomicDisadvantages"/>
        ),
        filterFn: (row, id, filter) => filterCriteriaList(row, id, String(filter)),
    },
    {
        id: 'races',
        accessorFn: (row) => accessorFnForCriteriaList(row.races),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="races"/>
        ),
        filterFn: (row, id, filter) => filterCriteriaList(row, id, String(filter)),
    },
    {
        id: 'indigeneities',
        accessorFn: (row) => accessorFnForCriteriaList(row.indigeneities),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="indigeneities"/>
        ),
        filterFn: (row, id, filter) => filterCriteriaList(row, id, String(filter)),
    },
    {
        id: 'disabilities',
        accessorFn: (row) => accessorFnForCriteriaList(row.disabilities),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="disabilities"/>
        ),
        filterFn: (row, id, filter) => filterCriteriaList(row, id, String(filter)),
    },
    {
        id: "isMale",
        accessorFn: row => row.isMale ? "Yes" : "No",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="isMale"/>
        ),
    },
    {
        id: "isFemale",
        accessorFn: row => row.isFemale ? "Yes" : "No",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="isFemale"/>
        ),
    },
    {
        id: "gender",
        accessorFn: row => row.gender?.name ?? "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="gender"/>
        ),
        filterFn: (row, id, value) => {
            return value.includes(row.getValue(id));
        },
    },
    {
        id: "sexualOrientation",
        accessorFn: row => row.sexualOrientation?.name ?? "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="sexualOrientation"/>
        ),
        filterFn: (row, id, value) => {
            return value.includes(row.getValue(id));
        },
    },
    {
        id: 'actions',
        cell: ({row}) => {
            const mentor = row.original;
            return (
                <DataTableRowActions
                    row={row}
                    onEdit={onEdit}
                    viewProfileUrl={`/mentors/${mentor.id}`}
                    viewMentorshipGroupUrl={`/mentors/${mentor.id}/mentees`}
                />
            );
        },
    },
];
